import BOTTOM_SHEETS from 'components/bottomsheet/registry';
import { BottomSheetOpenParams, BottomSheetOptions, BottomSheetParams, BottomSheetPayload, BottomSheetResult, BottomSheetStatus, CloseOptions } from 'types/bottomsheet';
import { animate, motionValue } from 'framer-motion';
import { create } from 'zustand';

const DEFAULT_OPTIONS: BottomSheetOptions = {
  fullScreen: false,
  maxHeight: '80%', // 가로 모드에선 CSS(--sheet-max-height)가 100%로 덮어씀 — 낮은 뷰포트라 위까지 채우고 내부 스크롤
  background: undefined,
  blur: false,
  draggable: true,
  confirmCancel: { confirm: false },
  containerStyle: {},
};

const DEFAULT_CLOSE_OPTIONS: CloseOptions = {
  skipConfirm: true,
  data: null,
};

// 동종 앱 녹화(30fps) 실측값: 딤 300ms ease-in-out, 시트는 열림 200ms ease-out(딤보다 70ms 늦게 출발) · 닫힘 200ms ease-in
export const SHEET_MOTION = {
  dimDuration: 0.3,
  dimEase: 'easeInOut' as const,
  openDuration: 0.2,
  openDelay: 0.07,
  openEase: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
  closeDuration: 0.2,
  closeEase: [0.55, 0.085, 0.68, 0.53] as [number, number, number, number],
  flingMinDuration: 0.08,
};

type BottomSheetState = {
  open: (id: string, payload?: BottomSheetParams) => Promise<BottomSheetResult>;
  close: (id: string, options?: CloseOptions) => void;
  sheets: Record<string, BottomSheetPayload>;
  // 바텀시트의 애니메이션과 오픈 상태 통합으로 open, isOpen -> status 로 통합, 중복 오픈을 막기 위함
  setStatus: (id: string, status: BottomSheetStatus) => void;
  updateOptions: (id: string, options: BottomSheetOptions) => void;
  updateData: (id: string, data: Record<string, any>) => void;
  resolves: Record<string, (value: BottomSheetResult) => void>;
};

const useBottomSheetStore = create<BottomSheetState>((set, get) => ({
  open: (id, payload) => {
    return new Promise((resolve) => {
      if (get().sheets[id]?.status === 'processing') {
        return resolve({ id, data: null, lifetime: 0 });
      }

      // 열려 있는 같은 ID 시트는 내용이 바뀌므로 이전 요청은 결과 없이 끝낸다
      const previous = get().sheets[id];
      get().resolves[id]?.({ id, data: null, lifetime: previous ? Date.now() - previous.openTime : 0 });

      const options = { ...DEFAULT_OPTIONS, ...BOTTOM_SHEETS[id]?.options, ...payload?.options };

      const openPrams: BottomSheetOpenParams = { y: motionValue(window.innerHeight), dim: motionValue(0), status: 'processing', openTime: Date.now() };

      set({
        sheets: { ...get().sheets, [id]: { ...payload, id, data: payload?.data ?? {}, options, ...openPrams } },
        resolves: { ...get().resolves, [id]: resolve },
      });
    });
  },
  close: (id, options = DEFAULT_CLOSE_OPTIONS) => {
    const { sheets, open, close, resolves } = get();
    const sheet = sheets[id];

    if (!sheet || (sheet.status !== 'opened' && sheet.status !== 'processing')) return;

    const { onDismissRequest } = sheet.options;
    if (options.reason && onDismissRequest) {
      // 드래그로 내려간 시트는 제자리로 돌린다
      animate(sheet.y, 0, { duration: SHEET_MOTION.openDuration, ease: SHEET_MOTION.openEase });
      animate(sheet.dim, 1, { duration: SHEET_MOTION.dimDuration, ease: SHEET_MOTION.dimEase });
      onDismissRequest();
      return;
    }

    const confirmCancel = sheet?.options.confirmCancel;

    if (confirmCancel?.confirm && !options.skipConfirm) {
      open('Common', {
        id: 'Common',
        data: {
          title: confirmCancel?.title ?? brsc.lucky_777_closepopup1,
          description: confirmCancel?.description ?? brsc.page_only_purchase,
          confirmText: brsc.leave,
        },
      }).then((result) => {
        if (result.data) {
          // data 가 없으면 sheet.data 를 반환
          close(id, { data: options.data ?? sheet.data, skipConfirm: true });
        }
      });

      return;
    }

    const resolve = resolves[id];
    const remainingResolves = { ...resolves };
    delete remainingResolves[id];

    set((state) => ({
      sheets: { ...state.sheets, [id]: { ...sheet, status: 'closing' } },
      resolves: remainingResolves,
    }));

    const finalize = () => {
      // 닫히는 사이 같은 ID로 다시 열렸으면 새 시트 상태는 두고 결과만 넘긴다
      if (get().sheets[id]?.y === sheet.y) {
        set((state) => ({ sheets: { ...state.sheets, [id]: { ...sheet, open: false, status: 'idle' } } }));
      }
      resolve?.({ id, data: options.data, lifetime: Date.now() - sheet.openTime, reason: options.reason });
    };

    const velocity = Math.max(0, options.velocity ?? 0);
    const travel = window.innerHeight - sheet.y.get();
    const duration = velocity > 0 ? Math.min(SHEET_MOTION.closeDuration, Math.max(SHEET_MOTION.flingMinDuration, travel / velocity)) : SHEET_MOTION.closeDuration;
    const ease = velocity > 0 ? 'linear' : SHEET_MOTION.closeEase;

    // finalize 는 dim 쪽에 건다. 드래그 시작이 y 애니메이션을 stop 시켜 onComplete 가 안 오는 경우가 있다
    animate(sheet.dim, 0, { duration: SHEET_MOTION.dimDuration, ease: SHEET_MOTION.dimEase, onComplete: finalize });
    animate(sheet.y, window.innerHeight, { duration, ease });
  },
  setStatus: (id, status) => set({ sheets: { ...get().sheets, [id]: { ...get().sheets[id], status } } }),
  sheets: {},
  updateOptions: (id, options) => {
    set({ sheets: { ...get().sheets, [id]: { ...get().sheets[id], options: { ...get().sheets[id].options, ...options } } } });
  },
  updateData: (id, data) => {
    set({ sheets: { ...get().sheets, [id]: { ...get().sheets[id], data: { ...get().sheets[id].data, ...data } } } });
  },
  resolves: {},
}));

export default useBottomSheetStore;
